/**
 * StepSystems - Wizard Step 6: Systems & Electrical
 * WBC3 Table 17.2.1 GMDSS Matrix Implementation
 * Replaces incorrect legacy logic with proper category×sea-area requirements
 */

import React, { useMemo, useEffect } from 'react';
import { Settings, Info, HelpCircle, Ship, Radio, Zap, Navigation, Satellite, Compass, Anchor, Wifi, Battery, Gauge } from 'lucide-react';
import { Switch } from '../../../../catalyst/checkbox';
import WizardSection from '../WizardSection';
import GMDSSConditionalQuestions from './components/GMDSSConditionalQuestions';
import EquipmentSections from './components/EquipmentSections';
import OperationalEquipmentSection from './components/OperationalEquipmentSection';
import { resolveGMDSSRequirements, GMDSS_SEA_AREAS, VOYAGE_DURATION_OPTIONS, VISUAL_ALERTING_OPTIONS, WEATHER_INFO_OPTIONS, UK_WATERS_OPTIONS } from './utils/gmdssMatrixLogic';
import {
  navigationEquipmentOptions,
  communicationEquipmentOptions,
  electricalSystemsOptions,
  engineeringSystemsOptions,
  operationalEquipmentOptions
} from './utils/systemsEquipmentData';
import { parseCategory } from '../../../../../utils/categoryHelpers';

// Equipment icon mapping for better visual hierarchy
const getEquipmentIcon = (equipmentId) => {
  // Navigation equipment icons
  if (equipmentId.includes('gps') || equipmentId.includes('compass')) return Compass;
  if (equipmentId.includes('radar') || equipmentId.includes('navigation')) return Navigation;
  if (equipmentId.includes('echo') || equipmentId.includes('sounder')) return Anchor;
  
  // Communication equipment icons
  if (equipmentId.includes('radio') || equipmentId.includes('vhf')) return Radio;
  if (equipmentId.includes('epirb') || equipmentId.includes('satellite')) return Satellite;
  if (equipmentId.includes('navtex') || equipmentId.includes('weather')) return Wifi;
  
  // Electrical/Engineering systems icons
  if (equipmentId.includes('battery') || equipmentId.includes('power')) return Battery;
  if (equipmentId.includes('alarm') || equipmentId.includes('monitoring')) return Gauge;
  if (equipmentId.includes('electrical') || equipmentId.includes('lighting')) return Zap;
  
  // Default icons by category
  if (equipmentId.startsWith('nav_')) return Navigation;
  if (equipmentId.startsWith('comm_')) return Radio;
  if (equipmentId.startsWith('elec_')) return Zap;
  if (equipmentId.startsWith('eng_')) return Settings;
  
  return Settings; // Default fallback
};

// Expose GMDSS matrix logic for testing
if (typeof window !== 'undefined') {
  window.gmdssMatrixLogic = {
    resolveGMDSSRequirements,
    GMDSS_SEA_AREAS,
    VOYAGE_DURATION_OPTIONS,
    VISUAL_ALERTING_OPTIONS,
    WEATHER_INFO_OPTIONS,
    UK_WATERS_OPTIONS
  };
}

const StepSystems = ({ draftConfig, onUpdate }) => {
  // Create updateConfig function compatible with existing logic
  const updateConfig = (updates) => {
    if (typeof updates === 'function') {
      onUpdate(updates(draftConfig));
    } else {
      onUpdate(updates);
    }
  };

  // Get GMDSS requirements using the matrix system
  const gmdssResult = resolveGMDSSRequirements(draftConfig);
  const conditionalQuestions = gmdssResult.conditionalQuestions || [];
  const showConditionalQuestions = conditionalQuestions.length > 0;
  
  // GMDSS requirements resolved for current configuration
  
  // Ensure equipment arrays exist
  if (!draftConfig.navigationEquipment) {
    draftConfig.navigationEquipment = [];
  }
  if (!draftConfig.communicationEquipment) {
    draftConfig.communicationEquipment = [];
  }
  if (!draftConfig.electricalSystems) {
    draftConfig.electricalSystems = [];
  }
  if (!draftConfig.engineeringSystems) {
    draftConfig.engineeringSystems = [];
  }
  if (!draftConfig.operationalEquipment) {
    draftConfig.operationalEquipment = [];
  }

  // Calculate required equipment based on GMDSS matrix and vessel configuration
  const requiredEquipmentUpdates = useMemo(() => {
    if (!draftConfig || gmdssResult.needsSeaAreaSelection) return null;
    
    // Group requirements by category
    const reqsByCategory = {
      navigation: [],
      communication: [],
      electrical: [],
      engineering: [],
      safety: []
    };
    
    // Add GMDSS matrix requirements - only auto-add MANDATORY items
    // RECOMMENDED items should remain unchecked by default so users can choose
    gmdssResult.requirements.forEach(req => {
      if (reqsByCategory[req.category] && req.status === 'MANDATORY') {
        reqsByCategory[req.category].push(req.equipmentId);
      }
    });
    
    // Add equipment data category requirements
    const currentCategory = parseCategory(draftConfig.category);
    const vesselType = draftConfig.vesselType || draftConfig.type;
    
    // Check navigation equipment
    navigationEquipmentOptions.forEach(option => {
      if (option.category === 'mandatory' && option.mandatoryForCategories?.includes(currentCategory)) {
        reqsByCategory.navigation.push(option.value);
      }
      if (option.mandatoryForVesselTypes?.includes(vesselType)) {
        reqsByCategory.navigation.push(option.value);
      }
    });
    
    // Check communication equipment
    communicationEquipmentOptions.forEach(option => {
      if (option.category === 'mandatory' && option.mandatoryForCategories?.includes(currentCategory)) {
        reqsByCategory.communication.push(option.value);
      }
      if (option.mandatoryForVesselTypes?.includes(vesselType)) {
        reqsByCategory.communication.push(option.value);
      }
    });
    
    // Check electrical systems
    electricalSystemsOptions.forEach(option => {
      if (option.category === 'mandatory' && option.mandatoryForCategories?.includes(currentCategory)) {
        reqsByCategory.electrical.push(option.value);
      }
      if (option.mandatoryForVesselTypes?.includes(vesselType)) {
        reqsByCategory.electrical.push(option.value);
      }
    });
    
    // Check engineering systems
    engineeringSystemsOptions.forEach(option => {
      if (option.category === 'mandatory' && option.mandatoryForCategories?.includes(currentCategory)) {
        reqsByCategory.engineering.push(option.value);
      }
      if (option.mandatoryForVesselTypes?.includes(vesselType)) {
        reqsByCategory.engineering.push(option.value);
      }
    });
    
    // Calculate updated equipment arrays
    const currentNav = draftConfig.navigationEquipment || [];
    const updatedNav = [...new Set([...currentNav, ...reqsByCategory.navigation])];
    const navChanged = JSON.stringify(currentNav.sort()) !== JSON.stringify(updatedNav.sort());
    
    const currentComm = draftConfig.communicationEquipment || [];
    const updatedComm = [...new Set([...currentComm, ...reqsByCategory.communication])];
    const commChanged = JSON.stringify(currentComm.sort()) !== JSON.stringify(updatedComm.sort());
    
    const currentElec = draftConfig.electricalSystems || [];
    const updatedElec = [...new Set([...currentElec, ...reqsByCategory.electrical])];
    const elecChanged = JSON.stringify(currentElec.sort()) !== JSON.stringify(updatedElec.sort());
    
    const currentEng = draftConfig.engineeringSystems || [];
    const updatedEng = [...new Set([...currentEng, ...reqsByCategory.engineering])];
    const engChanged = JSON.stringify(currentEng.sort()) !== JSON.stringify(updatedEng.sort());
    
    // Return updates only if something changed
    if (navChanged || commChanged || elecChanged || engChanged) {
      return {
        navigationEquipment: updatedNav,
        communicationEquipment: updatedComm,
        electricalSystems: updatedElec,
        engineeringSystems: updatedEng
      };
    }
    
    return null;
  }, [
    draftConfig?.category,
    draftConfig?.gmdssSeaArea,
    draftConfig?.voyageDuration,
    draftConfig?.visualAlertingEffective,
    draftConfig?.alternativeWeatherInfo,
    draftConfig?.operatesUKWaters,
    draftConfig?.vesselType,
    draftConfig?.navigationEquipment,
    draftConfig?.communicationEquipment,
    draftConfig?.electricalSystems,
    draftConfig?.engineeringSystems,
    gmdssResult.requirements,
    gmdssResult.needsSeaAreaSelection
  ]);

  // Apply equipment updates when they change
  useEffect(() => {
    if (requiredEquipmentUpdates) {
      const newConfig = { ...draftConfig, ...requiredEquipmentUpdates };
      onUpdate(newConfig);
    }
  }, [requiredEquipmentUpdates]);

  // Helper function to check if equipment is mandatory
  const isEquipmentMandatory = (equipmentId, equipmentData) => {
    // Check GMDSS matrix requirements first
    const gmdssRequired = gmdssResult.requirements.some(req => req.equipmentId === equipmentId && req.status === 'MANDATORY');
    
    // Check equipment category requirements
    if (equipmentData && equipmentData.category === 'mandatory' && equipmentData.mandatoryForCategories) {
      const category = parseCategory(draftConfig.category);
      const categoryRequired = equipmentData.mandatoryForCategories.includes(category);
      if (gmdssRequired || categoryRequired) return true;
    }
    
    // Check vessel-type specific requirements
    if (equipmentData && equipmentData.mandatoryForVesselTypes) {
      const vesselType = draftConfig.vesselType || draftConfig.type;
      const vesselTypeRequired = equipmentData.mandatoryForVesselTypes.includes(vesselType);
      if (vesselTypeRequired) return true;
    }
    
    return gmdssRequired;
  };

  // Helper function to get equipment reason
  const getEquipmentReason = (equipmentId) => {
    const req = gmdssResult.requirements.find(req => req.equipmentId === equipmentId);
    return req?.reason || '';
  };

  // Helper function to handle equipment selection
  const handleEquipmentChange = (category, equipmentId, checked) => {

    const currentList = draftConfig[category] || [];
    let updatedList;
    
    if (checked) {
      updatedList = [...currentList, equipmentId];
    } else {
      // Don't allow deselection of mandatory equipment
      // We need to find the equipment data to check if it's mandatory
      const allEquipment = [...navigationEquipmentOptions, ...communicationEquipmentOptions, ...electricalSystemsOptions, ...engineeringSystemsOptions, ...operationalEquipmentOptions];
      const equipmentData = allEquipment.find(eq => eq.value === equipmentId);
      if (isEquipmentMandatory(equipmentId, equipmentData)) {
        return;
      }
      updatedList = currentList.filter(id => id !== equipmentId);
    }

    updateConfig({ [category]: updatedList });
  };

  // Render equipment option with modern UI
  const renderEquipmentOption = (option, category) => {
    const isSelected = (draftConfig[category] || []).includes(option.value);
    const isMandatory = isEquipmentMandatory(option.value, option);
    
    // Enhanced reason display
    let reason = getEquipmentReason(option.value);
    if (!reason) {
      const currentCategory = parseCategory(draftConfig.category);
      
      if (option.category === 'mandatory' && option.mandatoryForCategories) {
        // Only show category requirement if current category is in the mandatory list
        if (option.mandatoryForCategories.includes(currentCategory)) {
          reason = `Category ${currentCategory} requirement`;
        }
      } else if (option.mandatoryForVesselTypes) {
        const vesselType = draftConfig.vesselType || draftConfig.type;
        if (option.mandatoryForVesselTypes.includes(vesselType)) {
          reason = `${vesselType} vessel requirement`;
        }
      } else if (option.category === 'operational') {
        reason = 'Optional operational enhancement';
      }
    }
    
    const EquipmentIcon = getEquipmentIcon(option.value);
    
    return (
      <div key={option.value} className={`relative border rounded-lg transition-colors ${
        isMandatory 
          ? 'bg-blue-50 border-blue-200' 
          : isSelected 
            ? 'bg-green-50 border-green-200' 
            : 'bg-white border-gray-200 hover:bg-gray-50'
      }`}>
        <label className="flex items-start p-4 cursor-pointer">
          <div className="flex-shrink-0 mt-0.5">
            <EquipmentIcon className={`h-5 w-5 ${
              isMandatory ? 'text-blue-600' : 
              isSelected ? 'text-green-600' : 'text-gray-400'
            }`} />
          </div>
          <div className="flex-1 ml-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {option.label}
                </span>
                {isMandatory && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    MANDATORY
                  </span>
                )}
                {isSelected && !isMandatory && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    SELECTED
                  </span>
                )}
              </div>
              <Switch
                checked={isSelected}
                onChange={(checked) => handleEquipmentChange(category, option.value, checked)}
                disabled={isMandatory}
                className="ml-3"
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {option.help}
            </p>
            {reason && (
              <p className="text-xs text-blue-600 mt-1 font-medium">
                <strong>WBC3:</strong> {reason}
              </p>
            )}
          </div>
        </label>
      </div>
    );
  };

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-purple-100 mb-3">
          <Settings className="h-7 w-7 md:h-6 md:w-6 text-purple-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          Systems & Electrical
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          Configure your vessel's navigation, communication, and electrical systems based on WBC3 GMDSS requirements.
        </WizardSection.Text>
      </div>

      {/* GMDSS Sea Area Selection Required */}
      {gmdssResult.needsSeaAreaSelection && (
        <WizardSection.Callout variant="warning" title="GMDSS Sea Area Required">
          <p className="text-sm mb-3">
            Please select your vessel's intended GMDSS sea area from Step 2 (Classification) to determine mandatory communication equipment.
          </p>
          <div className="text-xs text-amber-700">
            <strong>Note:</strong> This determines EPIRB, NAVTEX, and MF/HF radio requirements per WBC3 Table 17.2.1
          </div>
        </WizardSection.Callout>
      )}

      {/* Conditional Questions */}
      {showConditionalQuestions && (
        <GMDSSConditionalQuestions
          gmdssResult={gmdssResult}
          conditionalQuestions={conditionalQuestions}
          draftConfig={draftConfig}
          updateConfig={updateConfig}
        />
      )}

      {/* Quick Overview */}
      {!gmdssResult.needsSeaAreaSelection && !showConditionalQuestions && (
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h4 className="font-medium text-gray-900 mb-3">Configuration Overview</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Ship className="w-4 h-4 text-blue-500" />
              <span>Navigation: {(draftConfig.navigationEquipment || []).length} systems</span>
            </div>
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-green-500" />
              <span>Communication: {(draftConfig.communicationEquipment || []).length} systems</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span>Electrical: {(draftConfig.electricalSystems || []).length} systems</span>
            </div>
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <span>Operational: {(draftConfig.operationalEquipment || []).length} optional</span>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Sections - Only show if GMDSS is configured */}
      {!gmdssResult.needsSeaAreaSelection && !showConditionalQuestions && (
        <>
          {/* Regulatory Context */}
          <WizardSection.Callout variant="regulatory" title="WBC3 GMDSS Matrix Requirements">
            Equipment requirements are determined by WBC3 Table 17.2.1 based on your vessel's category and intended sea area. 
            Mandatory equipment cannot be deselected and is automatically included in your vessel configuration.
          </WizardSection.Callout>

          {/* Auto-Required Systems Notice */}
          {gmdssResult.requirements.filter(req => req.status === 'MANDATORY').length > 0 && (
            <WizardSection.Callout variant="info" title="Auto-Required Systems & Equipment">
              <p className="text-sm mb-3">
                The following systems are automatically included based on WBC3 Table 17.2.1:
              </p>
              <ul className="space-y-2">
                {gmdssResult.requirements.filter(req => req.status === 'MANDATORY').map((equipment, index) => (
                  <li key={index} className="text-sm">
                    • {equipment.name} - {equipment.reason} ({equipment.reference})
                  </li>
                ))}
              </ul>
            </WizardSection.Callout>
          )}

          {/* Main Equipment Sections */}
          <EquipmentSections 
            draftConfig={draftConfig}
            renderEquipmentOption={renderEquipmentOption}
          />

          {/* Operational Equipment Section */}
          <OperationalEquipmentSection 
            draftConfig={draftConfig}
            renderEquipmentOption={renderEquipmentOption}
          />
        </>
      )}

      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        After configuring systems and electrical equipment, you can review all requirements 
        and complete your vessel configuration in the final step.
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepSystems;